import React, { useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { usePonds, useMasterData } from '../../hooks/useApi';
import { retailSaleApi, MarketExpenseItem } from '../../utils/retailSalesApi';
import { getErrorMessage } from '../../utils/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const expenseSchema = yup.object({
  description: yup.string().required('Description is required'),
  amount: yup.number().required('Amount is required').positive('Amount must be positive'),
});

const speciesLineSchema = yup.object({
  species: yup.string().required('Species is required'),
  dispatched_quantity_kg: yup.number().typeError('Invalid number').required('Dispatched quantity is required').min(0, 'Cannot be negative'),
  sold_quantity_kg: yup.number().typeError('Invalid number').min(0, 'Cannot be negative').optional(),
  returned_quantity_kg: yup.number().typeError('Invalid number').min(0, 'Cannot be negative').optional(),
  rate_per_kg: yup.number().typeError('Invalid number').min(0, 'Cannot be negative').optional(),
});

const schema = yup.object({
  sale_date: yup.string().required('Sale date is required'),
  ponds: yup.array().of(yup.string()).optional(),
  species_lines: yup.array().of(speciesLineSchema).min(1, 'Add at least one species'),
  expenses: yup.array().of(expenseSchema),
  remarks: yup.string().optional(),
});

interface MarketSpeciesLineForm {
  species: string;
  dispatched_quantity_kg?: number; // Optional in form, but required by validation before submit
  sold_quantity_kg?: number;
  returned_quantity_kg?: number;
  rate_per_kg?: number;
}

interface MarketSaleFormData {
  sale_date: string;
  ponds?: string[];
  species_lines: MarketSpeciesLineForm[];
  expenses: MarketExpenseItem[];
  remarks?: string;
}

const MarketSaleForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaleId, setDraftSaleId] = useState<string | null>(null);
  const [step2Collapsed, setStep2Collapsed] = useState(false);
  const [step3Collapsed, setStep3Collapsed] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [checkingExistingDraft, setCheckingExistingDraft] = useState(false);

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const { data: fishSpecies } = useMasterData('fish_species', selectedLocation?.id || null);

  // Deduplicate fish species by name (case-insensitive)
  const uniqueFishSpecies = useMemo(() => {
    if (!fishSpecies || fishSpecies.length === 0) return [];
    const seen = new Set<string>();
    const unique: typeof fishSpecies = [];
    
    for (const species of fishSpecies) {
      const nameLower = (species.name || '').toLowerCase().trim();
      if (nameLower && !seen.has(nameLower)) {
        seen.add(nameLower);
        unique.push(species);
      }
    }
    
    return unique;
  }, [fishSpecies]);

  const form = useForm<MarketSaleFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      sale_date: new Date().toISOString().split('T')[0],
      ponds: [],
      species_lines: [
        {
          species: '',
        },
      ],
      expenses: [],
      remarks: '',
    },
  });

  const { fields: expenseFields, append: appendExpense, remove: removeExpense, replace: replaceExpenses } = useFieldArray({
    control: form.control,
    name: 'expenses',
  });

  const { fields: speciesFields, append: appendSpecies, remove: removeSpecies, replace: replaceSpecies } = useFieldArray({
    control: form.control,
    name: 'species_lines',
  });

  const watchedSpecies = form.watch('species_lines');
  const watchedExpenses = form.watch('expenses');

  const toNumberOrUndefined = (value: any) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  };

  const grossAmount = (watchedSpecies || []).reduce((sum, line) => {
    const sold = Number(line?.sold_quantity_kg) || 0;
    const rate = Number(line?.rate_per_kg) || 0;
    return sum + sold * rate;
  }, 0);
  const totalExpenses = watchedExpenses.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);
  const netAmount = grossAmount - totalExpenses;
  const totalDispatched = (watchedSpecies || []).reduce((sum, line) => sum + (Number(line?.dispatched_quantity_kg) || 0), 0);
  const totalSold = (watchedSpecies || []).reduce((sum, line) => sum + (Number(line?.sold_quantity_kg) || 0), 0);
  const totalReturned = (watchedSpecies || []).reduce((sum, line) => sum + (Number(line?.returned_quantity_kg) || 0), 0);

  const addExpense = () => {
    appendExpense({
      description: '',
      amount: 0,
    });
  };

  // Check for existing draft and load draft data from server on mount (only once)
  useEffect(() => {
    if (!selectedLocation?.id || hasInitialized) return;
    
    const checkExistingDraft = async (): Promise<boolean> => {
      try {
        setCheckingExistingDraft(true);
        const today = new Date().toISOString().split('T')[0];
        const drafts = await retailSaleApi.list({
          location_id: selectedLocation.id,
          status: 'draft',
          sale_type: 'market',
          start_date: today,
          end_date: today,
        });
        
        if (drafts && drafts.length > 0) {
          // Use the most recent draft
          const existingDraft = drafts[0];
          // Redirect to this draft
          navigate(`/retail-sales/market?draftId=${existingDraft.id}`, { replace: true });
          return true; // Found existing draft
        }
        return false; // No existing draft
      } catch (err) {
        console.error('Failed to check existing drafts:', err);
        return false;
      } finally {
        setCheckingExistingDraft(false);
      }
    };
    
    const draftIdFromUrl = searchParams.get('draftId');
    if (!draftIdFromUrl) {
      // Check for existing draft before initializing
      checkExistingDraft().then((foundDraft) => {
        // If we found an existing draft, it will redirect, so don't initialize
        // If no draft found, initialize normally
        if (!foundDraft) {
          setHasInitialized(true);
          setIsLoadingDraft(false);
        }
      });
      return;
    }
    
    const loadDraftFromServer = async (draftId: string) => {
      try {
        setIsLoadingDraft(true);
        setDraftSaleId(draftId);
        
        const draftSale = await retailSaleApi.get(draftId);
        if (draftSale && draftSale.status === 'draft' && draftSale.sale_type === 'market') {
          // Load basic fields
          if (draftSale.sale_date) form.setValue('sale_date', draftSale.sale_date);
          if (draftSale.ponds) form.setValue('ponds', draftSale.ponds);
          if (draftSale.remarks) form.setValue('remarks', draftSale.remarks);
          
          // Load expenses
          if (draftSale.expenses && Array.isArray(draftSale.expenses) && draftSale.expenses.length > 0) {
            const restoredExpenses = draftSale.expenses.map((exp: MarketExpenseItem) => ({
              description: exp.description,
              amount: Number(exp.amount) || 0,
            }));
            // Use replace to set all expenses at once (replaces existing ones)
            replaceExpenses(restoredExpenses);
          }
          
          // Load species_lines
          if (draftSale.species_lines && draftSale.species_lines.length > 0) {
            const restoredLines = draftSale.species_lines.map((line: any) => {
              const restoredLine: MarketSpeciesLineForm = {
                species: line.species || '',
              };
              if (line.dispatched_quantity_kg !== undefined && line.dispatched_quantity_kg !== null) {
                restoredLine.dispatched_quantity_kg = Number(line.dispatched_quantity_kg);
              }
              if (line.sold_quantity_kg !== undefined && line.sold_quantity_kg !== null) {
                restoredLine.sold_quantity_kg = Number(line.sold_quantity_kg);
              }
              if (line.returned_quantity_kg !== undefined && line.returned_quantity_kg !== null) {
                restoredLine.returned_quantity_kg = Number(line.returned_quantity_kg);
              }
              if (line.rate_per_kg !== undefined && line.rate_per_kg !== null) {
                restoredLine.rate_per_kg = Number(line.rate_per_kg);
              }
              return restoredLine;
            });
            
            // Use replace to set all species lines at once (replaces existing ones)
            replaceSpecies(restoredLines);
            
            // Set values explicitly after replace to ensure form state is updated
            setTimeout(() => {
              restoredLines.forEach((line, index) => {
                form.setValue(`species_lines.${index}.species`, line.species || '', { shouldValidate: false });
                if (line.dispatched_quantity_kg !== undefined) {
                  form.setValue(`species_lines.${index}.dispatched_quantity_kg`, line.dispatched_quantity_kg, { shouldValidate: false });
                }
                if (line.sold_quantity_kg !== undefined) {
                  form.setValue(`species_lines.${index}.sold_quantity_kg`, line.sold_quantity_kg, { shouldValidate: false });
                }
                if (line.returned_quantity_kg !== undefined) {
                  form.setValue(`species_lines.${index}.returned_quantity_kg`, line.returned_quantity_kg, { shouldValidate: false });
                }
                if (line.rate_per_kg !== undefined) {
                  form.setValue(`species_lines.${index}.rate_per_kg`, line.rate_per_kg, { shouldValidate: false });
                }
              });
              setHasInitialized(true);
              setIsLoadingDraft(false);
            }, 100);
          } else {
            setHasInitialized(true);
            setIsLoadingDraft(false);
          }
        } else {
          setHasInitialized(true);
          setIsLoadingDraft(false);
        }
      } catch (err) {
        console.error('Failed to load draft sale:', err);
        setHasInitialized(true);
        setIsLoadingDraft(false);
      }
    };
    
    loadDraftFromServer(draftIdFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, searchParams]);

  useEffect(() => {
    if (!ponds || ponds.length === 0) return;
    const current = form.getValues('ponds') || [];
    if (current && current.length > 0) return;
    const preferred = ponds.find((p) => p.name?.toUpperCase() === 'S2') || ponds[0];
    if (preferred) {
      form.setValue('ponds', [preferred.id]);
    }
  }, [ponds, form]);

  const handleSaveDraft = async () => {
    if (!selectedLocation?.id) {
      setError('Please select a location');
      return;
    }
    const data = form.getValues();

    const hasSpecies = (data.species_lines || []).some((line) => line?.species?.trim());
    if (!hasSpecies) {
      setError('Add at least one species before saving draft.');
      return;
    }

    // Aggregate species_lines data for backend (backend doesn't support species_lines yet)
    const validLines = (data.species_lines || []).filter((line) => line?.species?.trim());
    const totalDispatched = validLines.reduce((sum, line) => sum + (Number(line.dispatched_quantity_kg) || 0), 0);
    const totalSold = validLines.reduce((sum, line) => sum + (Number(line.sold_quantity_kg) || 0), 0);
    const totalReturned = validLines.reduce((sum, line) => sum + (Number(line.returned_quantity_kg) || 0), 0);
    
    // For rate, use the first non-zero rate, or 0 as default
    const firstRate = validLines.find((line) => Number(line.rate_per_kg) > 0);
    const defaultRate = firstRate ? Number(firstRate.rate_per_kg) : 0;

    // For draft saves, send aggregated data in old format
    // Backend requires seller_id, sold_quantity_kg, returned_quantity_kg, rate_per_kg
    // So we send defaults (0) for fields not yet filled
    const payload: any = {
      sale_date: data.sale_date,
      ponds: data.ponds || [],
      dispatch_quantity_kg: totalDispatched > 0 ? totalDispatched : undefined,
      sold_quantity_kg: totalSold > 0 ? totalSold : 0, // Required by backend, use 0 for draft
      returned_quantity_kg: totalReturned > 0 ? totalReturned : 0, // Required by backend, use 0 for draft
      rate_per_kg: defaultRate, // Required by backend, use 0 or first rate for draft
      expenses: (data.expenses || [])
        .filter((e) => e.description?.trim() && Number(e.amount) > 0)
        .map((e) => ({
          description: e.description,
          amount: Number(e.amount),
        })),
    };

    // Only include remarks if it's filled
    if (data.remarks?.trim()) {
      payload.remarks = data.remarks.trim();
    }

    // Include species_lines for future backend support, but also send old format
    payload.species_lines = validLines.map((line) => {
      const lineData: any = {
        species: line.species,
      };
      const dispatched = toNumberOrUndefined(line.dispatched_quantity_kg);
      if (dispatched !== undefined) {
        lineData.dispatched_quantity_kg = dispatched;
      }
      const sold = toNumberOrUndefined(line.sold_quantity_kg);
      if (sold !== undefined) {
        lineData.sold_quantity_kg = sold;
      }
      const returned = toNumberOrUndefined(line.returned_quantity_kg);
      if (returned !== undefined) {
        lineData.returned_quantity_kg = returned;
      }
      const rate = toNumberOrUndefined(line.rate_per_kg);
      if (rate !== undefined) {
        lineData.rate_per_kg = rate;
      }
      return lineData;
    });

    try {
      setSavingDraft(true);
      setError('');
      if (draftSaleId) {
        // Update existing draft
        await retailSaleApi.updateDraft(draftSaleId, payload);
      } else {
        // For create, backend requires seller_id, sold_quantity_kg, returned_quantity_kg, rate_per_kg
        // Send defaults (0) for fields not yet filled
        const createPayload: any = {
          location_id: selectedLocation.id,
          sale_date: payload.sale_date,
          ponds: payload.ponds,
          dispatch_quantity_kg: payload.dispatch_quantity_kg,
          sold_quantity_kg: payload.sold_quantity_kg, // 0 for draft
          returned_quantity_kg: payload.returned_quantity_kg, // 0 for draft
          rate_per_kg: payload.rate_per_kg, // 0 or first rate for draft
          expenses: payload.expenses,
          remarks: payload.remarks,
          // Include species_lines for future backend support
          species_lines: payload.species_lines,
        };
        
        // Backend requires seller_id - try without it first, backend should make it optional
        // If backend still requires it, we'll get an error and can handle it
        const created = await retailSaleApi.createMarket(createPayload);
        setDraftSaleId(created.id);
      }
      // Redirect to listing page on successful save
      navigate('/retail-sales/list');
    } catch (err: any) {
      const errorMsg = getErrorMessage(err, 'Failed to save draft');
      // Check if error is about seller_id
      if (errorMsg.toLowerCase().includes('seller') || err.response?.data?.detail?.toLowerCase().includes('seller')) {
        setError('Backend requires seller_id for market sales. The backend needs to be updated to make seller_id optional for direct-to-consumer market sales.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setSavingDraft(false);
    }
  };

  const onSubmit = async (data: MarketSaleFormData) => {
    if (!selectedLocation?.id) {
      setError('Please select a location');
      return;
    }

    const validationErrors: string[] = [];
    if (!data.species_lines || data.species_lines.length === 0) {
      validationErrors.push('Add at least one species line.');
    }

    (data.species_lines || []).forEach((line, idx) => {
      if (!line.species || !line.species.trim()) {
        validationErrors.push(`Line ${idx + 1}: Species is required.`);
      }
      const sold = toNumberOrUndefined(line.sold_quantity_kg);
      const returned = toNumberOrUndefined(line.returned_quantity_kg);
      const rate = toNumberOrUndefined(line.rate_per_kg);
      if (sold === undefined) validationErrors.push(`Line ${idx + 1}: Sold qty is required.`);
      if (returned === undefined) validationErrors.push(`Line ${idx + 1}: Returned qty is required.`);
      if (rate === undefined) validationErrors.push(`Line ${idx + 1}: Rate per kg is required.`);
      const dispatched = toNumberOrUndefined(line.dispatched_quantity_kg);
      if (dispatched !== undefined && sold !== undefined && returned !== undefined) {
        if (sold + returned - dispatched > 0.01) {
          validationErrors.push(`Line ${idx + 1}: Sold + Returned exceeds dispatched.`);
        }
      }
    });

    if (validationErrors.length > 0) {
      setError(validationErrors[0]);
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // Calculate aggregated values for legacy fields (backend still requires them)
      const validLines = (data.species_lines || []).filter((line) => line?.species?.trim());
      const totalDispatched = validLines.reduce((sum, line) => sum + (Number(line.dispatched_quantity_kg) || 0), 0);
      const totalSold = validLines.reduce((sum, line) => sum + (Number(line.sold_quantity_kg) || 0), 0);
      const totalReturned = validLines.reduce((sum, line) => sum + (Number(line.returned_quantity_kg) || 0), 0);
      
      // For rate, calculate weighted average or use first rate
      let avgRate = 0;
      if (totalSold > 0) {
        const totalAmount = validLines.reduce((sum, line) => {
          const sold = Number(line.sold_quantity_kg) || 0;
          const rate = Number(line.rate_per_kg) || 0;
          return sum + (sold * rate);
        }, 0);
        avgRate = totalAmount / totalSold;
      } else if (validLines.length > 0) {
        // If no sold quantity, use first rate
        avgRate = Number(validLines[0].rate_per_kg) || 0;
      }

      const saleData: any = {
        location_id: selectedLocation.id,
        sale_date: data.sale_date,
        ponds: data.ponds || [],
        // Legacy fields (required by backend)
        dispatch_quantity_kg: totalDispatched > 0 ? totalDispatched : undefined,
        sold_quantity_kg: totalSold,
        returned_quantity_kg: totalReturned,
        rate_per_kg: avgRate,
        // Multi-species support
        species_lines: validLines.map((line) => ({
          species: line.species,
          dispatched_quantity_kg: toNumberOrUndefined(line.dispatched_quantity_kg),
          sold_quantity_kg: toNumberOrUndefined(line.sold_quantity_kg),
          returned_quantity_kg: toNumberOrUndefined(line.returned_quantity_kg),
          rate_per_kg: toNumberOrUndefined(line.rate_per_kg),
        })),
        expenses: data.expenses.map((e) => ({
          description: e.description,
          amount: Number(e.amount) || 0,
        })),
        remarks: data.remarks || undefined,
      };

      if (draftSaleId) {
        // Update the draft with all data
        await retailSaleApi.updateDraft(draftSaleId, saleData);
        // If Step 3 (Sold & Returned) data is provided, mark as completed
        const hasSoldReturnedData = validLines.some((line) => 
          line?.species?.trim() && 
          line?.sold_quantity_kg !== undefined && 
          line?.returned_quantity_kg !== undefined && 
          line?.rate_per_kg !== undefined &&
          (Number(line.sold_quantity_kg) > 0 || Number(line.returned_quantity_kg) > 0)
        );
        if (hasSoldReturnedData) {
          await retailSaleApi.updateStatus(draftSaleId, { status: 'completed' });
        }
      } else {
        await retailSaleApi.createMarket(saleData);
      }
      navigate('/retail-sales/list');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to create sale'));
    } finally {
      setSubmitting(false);
    }
  };

  // Determine if we're in draft mode (editing existing draft)
  const isDraftMode = !!draftSaleId;
  
  // Check if we have dispatch data (Step 2 completed)
  const hasDispatchData = watchedSpecies && watchedSpecies.some((line) => 
    line?.species?.trim() && line?.dispatched_quantity_kg && Number(line.dispatched_quantity_kg) > 0
  );
  
  // Check if we have sold/returned data (Step 3 completed)
  const hasSoldReturnedData = watchedSpecies && watchedSpecies.some((line) => 
    line?.species?.trim() && 
    line?.sold_quantity_kg !== undefined && 
    line?.returned_quantity_kg !== undefined && 
    line?.rate_per_kg !== undefined &&
    (Number(line.sold_quantity_kg) > 0 || Number(line.returned_quantity_kg) > 0)
  );
  
  // Show Save Draft button when we have dispatch data but not sold/returned data yet
  const showSaveDraft = hasDispatchData && !hasSoldReturnedData;
  
  // Show Submit Sale button when we have sold/returned data
  const showSubmitSale = hasSoldReturnedData;

  // Expand Step 3 when we have sold/returned data (ready to submit)
  useEffect(() => {
    if (hasSoldReturnedData && step3Collapsed) {
      setStep3Collapsed(false);
    }
  }, [hasSoldReturnedData, step3Collapsed]);

  if (businessType !== 'fish') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for fish farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  if (isLoadingDraft || checkingExistingDraft) {
    return (
      <div className="container-mobile py-8 text-center">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-gray-600">{checkingExistingDraft ? 'Checking for existing draft...' : 'Loading draft...'}</p>
      </div>
    );
  }

  return (
    <div className="container-mobile py-4 space-y-4">
      <div className="flex items-center space-x-4">
        <Link to="/retail-sales" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Market Sale</h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="card p-4 space-y-3">
          <h2 className="text-base font-semibold text-gray-900">Step 1 (Anytime): Date & Pond</h2>
          
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-3">
              <label className="form-label">Sale Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                {...form.register('sale_date')}
                className={`form-input ${form.formState.errors.sale_date ? 'border-red-500' : ''}`}
              />
              {form.formState.errors.sale_date && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.sale_date.message}</p>
              )}
            </div>

            <div className="col-span-1">
              <label className="form-label">Pond</label>
              <select
                className="form-input"
                value={(form.watch('ponds') || [])[0] || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  form.setValue('ponds', value ? [value] : []);
                }}
              >
                <option value="">Select pond</option>
                {ponds?.map((pond) => (
                  <option key={pond.id} value={pond.id}>
                    {pond.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Step 2 (2 PM): Dispatch</h2>
              <p className="text-sm text-gray-500">Add species sent to market with dispatched quantity.</p>
            </div>
            <div className="flex items-center space-x-2">
              {hasSoldReturnedData && (
                <button
                  type="button"
                  onClick={() => setStep2Collapsed(!step2Collapsed)}
                  className="btn-touch p-2 text-gray-400 hover:text-gray-600"
                  aria-label={step2Collapsed ? 'Expand Step 2' : 'Collapse Step 2'}
                >
                  {step2Collapsed ? (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={() =>
                  appendSpecies({
                    species: '',
                  })
                }
                className="btn-secondary flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Add Species</span>
              </button>
            </div>
          </div>

          {!step2Collapsed && (
            <>
              {speciesFields.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  <p>No species added yet. Click "Add Species" to start dispatch.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {speciesFields.map((field, index) => (
                <div key={field.id} className="border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-700">Species {index + 1}</h3>
                    <button
                      type="button"
                      onClick={() => removeSpecies(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Species <span className="text-red-500">*</span></label>
                      <select
                        {...form.register(`species_lines.${index}.species` as const)}
                        className={`w-full px-2 py-1.5 text-sm border rounded-md ${form.formState.errors.species_lines?.[index]?.species ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <option value="">Select Species</option>
                        {uniqueFishSpecies.map((species) => (
                          <option key={species.id} value={species.name}>
                            {species.name}
                          </option>
                        ))}
                      </select>
                      {form.formState.errors.species_lines?.[index]?.species && (
                        <p className="mt-0.5 text-xs text-red-600">
                          {form.formState.errors.species_lines[index]?.species?.message as string}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Dispatched Qty (kg) <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        step="0.01"
                        {...form.register(`species_lines.${index}.dispatched_quantity_kg` as const, { valueAsNumber: true })}
                        className={`w-full px-2 py-1.5 text-sm border rounded-md ${form.formState.errors.species_lines?.[index]?.dispatched_quantity_kg ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {form.formState.errors.species_lines?.[index]?.dispatched_quantity_kg && (
                        <p className="mt-0.5 text-xs text-red-600">
                          {form.formState.errors.species_lines[index]?.dispatched_quantity_kg?.message as string}
                        </p>
                      )}
                    </div>
                  </div>
                  </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {(!isDraftMode || hasDispatchData) && (
        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Step 3 (6 PM): Sold & Returned</h2>
              <p className="text-sm text-gray-500">Enter evening close numbers for each dispatched species.</p>
            </div>
            <button
              type="button"
              onClick={() => setStep3Collapsed(!step3Collapsed)}
              className="btn-touch p-2 text-gray-400 hover:text-gray-600"
              aria-label={step3Collapsed ? 'Expand Step 3' : 'Collapse Step 3'}
            >
              {step3Collapsed ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronUpIcon className="h-5 w-5 text-gray-500" />
              )}
            </button>
          </div>

          {!step3Collapsed && (
            <>
              {speciesFields.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              <p>Add species in Step 2 to enter sold/returned details.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {speciesFields.map((field, index) => {
                const dispatch = watchedSpecies?.[index]?.dispatched_quantity_kg;
                const sold = watchedSpecies?.[index]?.sold_quantity_kg;
                const returned = watchedSpecies?.[index]?.returned_quantity_kg;
                const rate = watchedSpecies?.[index]?.rate_per_kg;
                const soldNum = Number(sold) || 0;
                const returnedNum = Number(returned) || 0;
                const dispatchNum = Number(dispatch) || 0;
                const rateNum = Number(rate) || 0;
                const total = soldNum + returnedNum;
                const balanced = dispatch === undefined || Math.abs(total - dispatchNum) < 0.01;
                const amount = soldNum * rateNum;

                // Auto-calculate handlers
                const handleSoldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                  const newSold = parseFloat(e.target.value) || 0;
                  form.setValue(`species_lines.${index}.sold_quantity_kg`, newSold, { shouldValidate: true });
                  if (dispatchNum > 0 && newSold >= 0 && newSold <= dispatchNum) {
                    const calculatedReturned = dispatchNum - newSold;
                    form.setValue(`species_lines.${index}.returned_quantity_kg`, calculatedReturned, { shouldValidate: true });
                  }
                };

                const handleReturnedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                  const newReturned = parseFloat(e.target.value) || 0;
                  form.setValue(`species_lines.${index}.returned_quantity_kg`, newReturned, { shouldValidate: true });
                  if (dispatchNum > 0 && newReturned >= 0 && newReturned <= dispatchNum) {
                    const calculatedSold = dispatchNum - newReturned;
                    form.setValue(`species_lines.${index}.sold_quantity_kg`, calculatedSold, { shouldValidate: true });
                  }
                };

                return (
                  <div key={field.id} className="border border-gray-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-700">{watchedSpecies?.[index]?.species || `Species ${index + 1}`}</h3>
                      {dispatch !== undefined && (
                        <span className="text-xs text-gray-500">
                          Dispatched: {dispatchNum.toFixed(2)} kg
                          {rateNum > 0 && ` (₹${amount.toFixed(2)})`}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Sold Qty (kg) <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          step="0.01"
                          {...form.register(`species_lines.${index}.sold_quantity_kg` as const, { valueAsNumber: true })}
                          onChange={(e) => {
                            form.register(`species_lines.${index}.sold_quantity_kg` as const).onChange(e);
                            handleSoldChange(e);
                          }}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                        />
                        {form.formState.errors.species_lines?.[index]?.sold_quantity_kg && (
                          <p className="mt-0.5 text-xs text-red-600">
                            {form.formState.errors.species_lines[index]?.sold_quantity_kg?.message as string}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Rtrn Qty (kg) <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          step="0.01"
                          {...form.register(`species_lines.${index}.returned_quantity_kg` as const, { valueAsNumber: true })}
                          onChange={(e) => {
                            form.register(`species_lines.${index}.returned_quantity_kg` as const).onChange(e);
                            handleReturnedChange(e);
                          }}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                        />
                        {form.formState.errors.species_lines?.[index]?.returned_quantity_kg && (
                          <p className="mt-0.5 text-xs text-red-600">
                            {form.formState.errors.species_lines[index]?.returned_quantity_kg?.message as string}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Rate per kg (₹) <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          step="0.01"
                          {...form.register(`species_lines.${index}.rate_per_kg` as const, { valueAsNumber: true })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                        />
                        {form.formState.errors.species_lines?.[index]?.rate_per_kg && (
                          <p className="mt-0.5 text-xs text-red-600">
                            {form.formState.errors.species_lines[index]?.rate_per_kg?.message as string}
                          </p>
                        )}
                      </div>
                    </div>

                    {dispatch !== undefined && (
                      <div
                        className={`p-2 rounded-lg mt-2 ${
                          balanced ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
                        }`}
                      >
                        <p className="text-xs">
                          Dispatched: {dispatchNum.toFixed(2)} kg | Sold: {soldNum.toFixed(2)} kg | Returned:{' '}
                          {returnedNum.toFixed(2)} kg | Total: {total.toFixed(2)} kg{' '}
                          {balanced ? <span className="text-green-600 ml-1">✓ Balanced</span> : <span className="text-yellow-600 ml-1">⚠ Mismatch</span>}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
            </>
          )}
        </div>
        )}

        {(!isDraftMode || hasDispatchData) && (
        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Step 4: Market Expenses</h2>
            <button
              type="button"
              onClick={addExpense}
              className="btn-secondary flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Add Expense</span>
            </button>
          </div>

          {expenseFields.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              <p>No expenses added. Click "Add Expense" if there are any market expenses.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {expenseFields.map((field, index) => (
                <div key={field.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-700">Expense {index + 1}</h3>
                    <button
                      type="button"
                      onClick={() => removeExpense(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        {...form.register(`expenses.${index}.description`)}
                        className={`w-full px-2 py-1.5 text-sm border rounded-md ${form.formState.errors.expenses?.[index]?.description ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="e.g., Polybags, Transport"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹) <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        step="0.01"
                        {...form.register(`expenses.${index}.amount`, { valueAsNumber: true })}
                        className={`w-full px-2 py-1.5 text-sm border rounded-md ${form.formState.errors.expenses?.[index]?.amount ? 'border-red-500' : 'border-gray-300'}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        <div className="card p-4 space-y-3">
          <h2 className="text-base font-semibold text-gray-900">Step 5: Summary</h2>
          
          {hasSoldReturnedData ? (
            // Show full summary when sold/returned data exists
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                  <label className="text-sm font-medium text-gray-600">Gross Amount</label>
                  <div className="text-2xl font-bold text-blue-600 mt-1">
                    ₹{grossAmount.toFixed(2)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Σ (sold × rate) per species</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 border-2 border-red-200">
                  <label className="text-sm font-medium text-gray-600">Total Expenses</label>
                  <div className="text-2xl font-bold text-red-600 mt-1">
                    ₹{totalExpenses.toFixed(2)}
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                  <label className="text-sm font-medium text-gray-600">Net Amount</label>
                  <div className="text-2xl font-bold text-green-600 mt-1">
                    ₹{netAmount.toFixed(2)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Gross - Expenses</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700">
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <span className="font-medium">Total Dispatched:</span> {totalDispatched.toFixed(2)} kg
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <span className="font-medium">Total Sold:</span> {totalSold.toFixed(2)} kg
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <span className="font-medium">Total Returned:</span> {totalReturned.toFixed(2)} kg
                </div>
              </div>

              {speciesFields.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm border border-gray-300 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 border-r border-gray-300">Species</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700 border-r border-gray-300">Dispatched (kg)</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700 border-r border-gray-300">Sold (kg)</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700 border-r border-gray-300">Returned (kg)</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700 border-r border-gray-300">Rate (₹/kg)</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {speciesFields.map((field, idx) => {
                        const line = watchedSpecies?.[idx];
                        const dispatched = Number(line?.dispatched_quantity_kg) || 0;
                        const sold = Number(line?.sold_quantity_kg) || 0;
                        const returned = Number(line?.returned_quantity_kg) || 0;
                        const rate = Number(line?.rate_per_kg) || 0;
                        const amount = sold * rate;
                        return (
                          <tr key={field.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900 border-r border-gray-200">{line?.species || `Species ${idx + 1}`}</td>
                            <td className="px-4 py-3 text-center text-gray-700 border-r border-gray-200">{dispatched.toFixed(2)}</td>
                            <td className="px-4 py-3 text-center text-gray-700 border-r border-gray-200">{sold.toFixed(2)}</td>
                            <td className="px-4 py-3 text-center text-gray-700 border-r border-gray-200">{returned.toFixed(2)}</td>
                            <td className="px-4 py-3 text-center text-gray-700 border-r border-gray-200">{rate.toFixed(2)}</td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-900">{amount.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-gray-50 font-semibold border-t-2 border-gray-400">
                        <td className="px-4 py-3 text-gray-900 border-r border-gray-300">Total</td>
                        <td className="px-4 py-3 text-center text-gray-900 border-r border-gray-300">{totalDispatched.toFixed(2)}</td>
                        <td className="px-4 py-3 text-center text-gray-900 border-r border-gray-300">{totalSold.toFixed(2)}</td>
                        <td className="px-4 py-3 text-center text-gray-900 border-r border-gray-300">{totalReturned.toFixed(2)}</td>
                        <td className="px-4 py-3 text-center text-gray-900 border-r border-gray-300">-</td>
                        <td className="px-4 py-3 text-right text-gray-900">₹{grossAmount.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            // Draft mode (dispatch only): Show minimal dispatch summary
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700">
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <span className="font-medium">Total Dispatched:</span> {totalDispatched.toFixed(2)} kg
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <span className="font-medium">Species Count:</span> {speciesFields.length}
                </div>
              </div>
              {speciesFields.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Species</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Dispatched</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {speciesFields.map((field, idx) => {
                        const line = watchedSpecies?.[idx];
                        return (
                          <tr key={field.id}>
                            <td className="px-4 py-2">{line?.species || `Species ${idx + 1}`}</td>
                            <td className="px-4 py-2">{(Number(line?.dispatched_quantity_kg) || 0).toFixed(2)} kg</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          <div>
            <label className="form-label">Remarks</label>
            <textarea
              {...form.register('remarks')}
              rows={3}
              className="form-input"
              placeholder="Optional notes..."
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:justify-end md:space-x-4 space-y-3 md:space-y-0">
          <Link
            to="/retail-sales"
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </Link>
          {showSaveDraft && (
            <button
              type="button"
              disabled={savingDraft || submitting}
              onClick={handleSaveDraft}
              className="px-6 py-2 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors"
            >
              {savingDraft ? (
                <span className="flex items-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Saving Draft...
                </span>
              ) : (
                'Save Draft'
              )}
            </button>
          )}
          {showSubmitSale && (
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary"
            >
              {submitting ? (
                <span className="flex items-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Submitting...
                </span>
              ) : (
                'Submit Sale'
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default MarketSaleForm;
